import type { State } from '../State';
import type { DreamDriftCurrencyGrantProperties } from '../analytics/events';
import {
  dreamDriftGetCheckpoint,
  dreamDriftGetEventTheme,
  dreamDriftGetPuzzleLevel,
} from './dreamDrift.getters';
import { dreamDriftHeroes, type DreamDriftHero } from './dreamDrift.ruleset';
import type { DreamDriftPuzzleEventProps } from './dreamDrift.analytics.schema';

/** Complete Dream Drift properties emitted for an active puzzle. */
type ActiveDreamDriftPuzzleEventProps = {
  [Property in keyof DreamDriftPuzzleEventProps]-?: NonNullable<
    DreamDriftPuzzleEventProps[Property]
  >;
};

/** Shared analytics identity derived from the current Dream Drift instance. */
type DreamDriftInstanceAnalyticsProperties = {
  /** Server timestamp identifying the instance. */
  instanceId: number;
  /** One-based instance number within the event window. */
  instanceNumberInWindow: number;
  /** Theme assigned to the instance's event window. */
  themeId: ReturnType<typeof dreamDriftGetEventTheme>;
};

/**
 * Resolve a stored hero against the currently configured Dream Drift heroes.
 *
 * @param hero Stored hero identifier from player state.
 * @returns Configured hero identifier, or undefined for legacy invalid state.
 */
function getDreamDriftAnalyticsHero(
  hero: string | undefined,
): DreamDriftHero | undefined {
  // Analytics should emit only hero values accepted by the current schema.
  return dreamDriftHeroes.find((configuredHero) => configuredHero === hero);
}

/**
 * Get the shared analytics identity for the current Dream Drift instance.
 *
 * @param state Player state containing the instance to describe.
 * @returns Shared instance properties, or undefined when no instance exists.
 */
function getDreamDriftInstanceAnalyticsProperties(
  state: State,
): DreamDriftInstanceAnalyticsProperties | undefined {
  // Analytics identity requires a stored Dream Drift instance.
  const instance = state.dreamDrift?.instance;
  if (!instance) return undefined;

  // Legacy instances without a number are the first retained run in the window.
  return {
    instanceId: instance.startedAt,
    instanceNumberInWindow: instance.instanceNumber ?? 1,
    themeId: dreamDriftGetEventTheme(instance.startedAt),
  };
}

/**
 * Get Dream Drift context for shared puzzle analytics events.
 *
 * @param state Player state containing the active puzzle instance.
 * @param node Optional explicit node captured when the attempt started.
 * @returns Complete Dream Drift puzzle properties, or undefined when inactive.
 */
export function getDreamDriftPuzzleEventProperties(
  state: State,
  node = state.dreamDrift?.instance?.playing ?? 0,
): ActiveDreamDriftPuzzleEventProps | undefined {
  // A positive node and unresolved instance identify Dream Drift gameplay.
  const instance = state.dreamDrift?.instance;
  if (!instance || instance.result || node <= 0) return undefined;

  // Invalid legacy hero values cannot satisfy the analytics enum.
  const activeHero = getDreamDriftAnalyticsHero(instance.hero);
  if (!activeHero) return undefined;

  // Attach the complete join key used across Dream Drift puzzle events.
  return {
    feature: 'dream_drift',
    instanceId: instance.startedAt,
    dreamNodeIndex: node,
    activeHero,
    themeId: dreamDriftGetEventTheme(instance.startedAt),
  };
}

/**
 * Build the lifecycle event emitted when a Dream Drift instance starts.
 *
 * @param state Player state containing the newly started instance.
 * @returns Start event, or undefined when required instance data is missing.
 */
export function getDreamDriftStartAnalyticsEvent(state: State) {
  // Both shared identity and a valid selected hero are required.
  const instance = state.dreamDrift?.instance;
  const properties = getDreamDriftInstanceAnalyticsProperties(state);
  const activeHero = getDreamDriftAnalyticsHero(instance?.hero);
  if (!properties || !activeHero) return undefined;

  // Emit the selected hero with the new instance identity.
  return {
    eventType: 'DreamDriftStart',
    eventProperties: {
      ...properties,
      activeHero,
    },
  } as const;
}

/**
 * Build switch analytics from an accepted hero change and the upcoming node.
 *
 * @param state Player state after the accepted switch.
 * @param previousHero Stored hero captured before the switch.
 * @returns Switch event, or undefined for a no-op or incomplete context.
 */
export function getDreamDriftHeroSwitchAnalyticsEvent(
  state: State,
  previousHero: string | undefined,
) {
  // Require an ongoing instance and a valid previous hero.
  const instance = state.dreamDrift?.instance;
  const previousAnalyticsHero = getDreamDriftAnalyticsHero(previousHero);
  if (!instance || instance.result || !previousAnalyticsHero) return undefined;

  // Selection happens before playing is set for the upcoming attempt.
  const node = instance.progress + 1;
  const context = getDreamDriftPuzzleEventProperties(state, node);
  const properties = getDreamDriftInstanceAnalyticsProperties(state);
  if (!context || !properties || previousAnalyticsHero === context.activeHero)
    return undefined;

  // A dedicated switch event always includes the source puzzle level.
  const puzzleLevel = dreamDriftGetPuzzleLevel(state, node);
  if (puzzleLevel === undefined) return undefined;

  // Map the current hero to the transition field without leaking activeHero.
  return {
    eventType: 'DreamDriftHeroSwitch',
    eventProperties: {
      ...properties,
      dreamNodeIndex: context.dreamNodeIndex,
      puzzleLevel,
      previousHero: previousAnalyticsHero,
      newHero: context.activeHero,
      feature: context.feature,
    },
  } as const;
}

/**
 * Build the lifecycle event emitted when a Dream Drift instance resolves.
 *
 * @param state Player state containing the terminal instance.
 * @returns Completion event, or undefined when the instance is unresolved.
 */
export function getDreamDriftCompleteAnalyticsEvent(state: State) {
  // Completion analytics require a stored terminal instance.
  const instance = state.dreamDrift?.instance;
  const properties = getDreamDriftInstanceAnalyticsProperties(state);
  if (!instance?.result || !properties) return undefined;

  // Legacy instances fall back to their current authoritative progress.
  const maxLevelReached = Math.max(
    instance.maxLevelReached ?? 0,
    instance.progress,
  );
  const checkpointReached = dreamDriftGetCheckpoint(state, maxLevelReached + 1);

  // Emit the terminal result with the highest progression reached.
  return {
    eventType: 'DreamDriftComplete',
    eventProperties: {
      ...properties,
      result: instance.result,
      levelReachedMax: maxLevelReached,
      checkpointReached,
    },
  } as const;
}

/**
 * Get Dream Drift identity added to milestone CurrencyGrant events.
 *
 * @param state Player state containing the rewarded instance.
 * @returns Reward join properties, or undefined when no instance exists.
 */
export function getDreamDriftCurrencyGrantProperties(
  state: State,
): DreamDriftCurrencyGrantProperties | undefined {
  // Reward attribution requires the current instance identity.
  const properties = getDreamDriftInstanceAnalyticsProperties(state);
  if (!properties) return undefined;

  // Currency grants need only the instance and theme join keys from the spec.
  return {
    instanceId: properties.instanceId,
    themeId: properties.themeId,
  };
}
